// ============================================================
//  SAP: Basics & Fundamentals — full course content (40 Hours)
// ============================================================
//
//  Block types accepted by <SubTopicView/>:
//    { type: 'p',   text }
//    { type: 'h',   text }
//    { type: 'ul',  items }
//    { type: 'ol',  items }
//    { type: 'img', src, caption, alt }
//    { type: 'callout', variant, title, text }     // variant = info | tip | warn | example
//    { type: 'table',   headers, rows }            // rows = [[...]]
//    { type: 'kv', items: [{k, v}] }
//
import {
  erpDiagram,
  threeTierDiagram,
  p2pDiagram,
  o2cDiagram,
  activateDiagram,
  dataTypesDiagram,
  landscapeDiagram,
} from '../lib/diagrams';

const IMG = {
  officeTeam:
    'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=80',
  datacenter:
    'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1400&q=80',
  analytics:
    'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1400&q=80',
  factory:
    'https://images.unsplash.com/photo-1513828583688-c52646db42da?auto=format&fit=crop&w=1400&q=80',
  boardroom:
    'https://images.unsplash.com/photo-1560472355-536de3962603?auto=format&fit=crop&w=1400&q=80',
  warehouse:
    'https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1400&q=80',
  finance:
    'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1400&q=80',
  people:
    'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1400&q=80',
  security:
    'https://images.unsplash.com/photo-1562813733-b31f71025d54?auto=format&fit=crop&w=1400&q=80',
  blueprint:
    'https://images.unsplash.com/photo-1507206130118-b5907f817163?auto=format&fit=crop&w=1400&q=80',
  career:
    'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1400&q=80',
  dashboard:
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1400&q=80',
};

// ——————————————————————————————————————————————————————————————
//  MODULE 1 — Introduction to ERP & SAP (4h)
// ——————————————————————————————————————————————————————————————
const module1 = {
  id: 'm1',
  number: 1,
  title: 'Introduction to ERP & SAP',
  duration: '4 Hours',
  minutes: 240,
  icon: 'BookOpen',
  tagline: 'The foundation — what ERP is, why it matters, and how SAP came to dominate.',
  takeaway:
    'You will clearly articulate what ERP is, why it matters to modern businesses, and where SAP fits in the ERP universe — including real-world scenarios across manufacturing, retail and banking.',
  subTopics: [
    {
      id: 'm1-s1',
      title: 'What is ERP? A Deep Dive',
      readingTime: '20 min',
      blocks: [
        {
          type: 'p',
          text:
            'ERP stands for Enterprise Resource Planning. At its simplest, an ERP system is one integrated software backbone that runs the core business functions of an organization — finance, procurement, manufacturing, sales, human resources, supply chain, and more — all on a single shared database.',
        },
        {
          type: 'p',
          text:
            'Before ERP, each department in a company typically ran its own stand-alone software. Finance had accounting tools, the warehouse had inventory software, sales had CRM, HR had payroll systems. These tools did not talk to each other. Data had to be re-keyed from one system to another, reconciled, and reconciled again. Reports were always "one version of the truth that no-one agreed on".',
        },
        {
          type: 'img',
          src: erpDiagram,
          alt: 'ERP integrating finance, sales, procurement, HR, manufacturing and warehouse around a central database',
          caption:
            'Figure 1.1 — An ERP unifies every business function around a single, trusted database.',
        },
        { type: 'h', text: 'The three-word definition' },
        { type: 'p', text: 'ERP = One system. One database. One truth.' },
        { type: 'h', text: 'What ERP actually does, day-to-day' },
        {
          type: 'ul',
          items: [
            'Captures every business transaction (sales order, invoice, goods receipt, payroll run) in one integrated place.',
            'Automates the flow of information across departments — a sales order in Sales automatically reserves stock in the Warehouse and creates a receivable in Finance.',
            'Enforces standardized processes, approvals and controls across the whole company.',
            'Gives leadership real-time, trustworthy dashboards and reports.',
            'Becomes the single source of truth for customers, materials, vendors and employees.',
          ],
        },
        {
          type: 'callout',
          variant: 'example',
          title: 'Everyday analogy',
          text:
            'Think of ERP as the central nervous system of a business — every limb (department) sends signals to the brain, and the brain coordinates them so the body moves as one.',
        },
      ],
    },
    {
      id: 'm1-s2',
      title: 'Why Businesses Need ERP',
      readingTime: '20 min',
      blocks: [
        { type: 'h', text: 'The pains ERP was designed to cure' },
        {
          type: 'ul',
          items: [
            'Data silos — each department has its own numbers and none of them agree.',
            'Manual re-keying — the same purchase order is typed into three different systems.',
            'Slow month-end close — finance teams spend weeks reconciling spreadsheets.',
            'No real-time visibility — leaders discover stock-outs or cash-flow issues far too late.',
            'Audit &amp; compliance headaches — no central log of who did what and when.',
          ],
        },
        { type: 'h', text: 'Business benefits of ERP' },
        {
          type: 'table',
          headers: ['Benefit', 'What it means in practice'],
          rows: [
            ['Efficiency', 'Automated hand-offs reduce manual work and human error.'],
            ['Visibility', 'Executives see real-time inventory, cash, orders and headcount.'],
            ['Standardization', 'One process for "raise a PO" across every plant and country.'],
            ['Scalability', 'Add new plants, currencies or legal entities without reinventing systems.'],
            ['Compliance', 'Built-in controls make SOX, IFRS and tax audits far easier.'],
            ['Customer service', 'Sales can promise delivery because they see live stock and capacity.'],
          ],
        },
        {
          type: 'img',
          src: IMG.officeTeam,
          caption:
            'Figure 1.2 — Cross-functional teams collaborating on the same, trusted data.',
        },
        { type: 'h', text: 'Why today, more than ever?' },
        {
          type: 'p',
          text:
            'Modern businesses compete on speed. Supply chains are global, customers expect same-day shipping, regulators demand transparency, and CEOs want dashboards on their phones. Running on spreadsheets and disconnected tools simply does not scale. ERP — especially cloud ERP — is now considered foundational infrastructure, not a nice-to-have.',
        },
        {
          type: 'callout',
          variant: 'info',
          title: 'Did you know?',
          text:
            'According to analysts such as Gartner and IDC, over 80% of Fortune-500 companies run an ERP system, and SAP alone powers the operations of companies producing more than 87% of global commerce value.',
        },
      ],
    },
    {
      id: 'm1-s3',
      title: 'Introduction to SAP & its Evolution',
      readingTime: '25 min',
      blocks: [
        {
          type: 'p',
          text:
            'SAP is a German software company founded in 1972 by five former IBM engineers in Walldorf, Germany. The name originally stood for "Systemanalyse und Programmentwicklung" — System Analysis and Program Development. Today it simply stands for SAP.',
        },
        { type: 'h', text: 'A quick timeline' },
        {
          type: 'table',
          headers: ['Era', 'Product', 'What it delivered'],
          rows: [
            ['1972', 'SAP R/1', 'First real-time financial accounting software on a mainframe.'],
            ['1979', 'SAP R/2', 'Mainframe ERP suite integrating finance, materials and HR.'],
            ['1992', 'SAP R/3', 'Client-server 3-tier architecture — the blockbuster that defined ERP.'],
            ['2004', 'SAP ERP / ECC 6.0', 'Modernized R/3 on NetWeaver platform.'],
            ['2011', 'SAP HANA', 'In-memory database — 1000× faster analytics.'],
            ['2015', 'SAP S/4HANA', 'Next-generation ERP, simplified data model on HANA.'],
            ['2020+', 'RISE with SAP / GROW with SAP', 'Cloud-first, subscription, fully managed ERP.'],
          ],
        },
        {
          type: 'img',
          src: IMG.datacenter,
          caption: 'Figure 1.3 — SAP software runs the core of global commerce.',
        },
        { type: 'h', text: 'What makes SAP special?' },
        {
          type: 'ul',
          items: [
            'Breadth — one vendor covers Finance, Supply Chain, HR, Sales, Manufacturing, Analytics and more.',
            'Industry depth — pre-configured best-practice processes for 25+ industries (automotive, banking, oil &amp; gas, public sector, retail…).',
            'Global-ready — handles 40+ languages and every major currency, tax and statutory regime out of the box.',
            'Ecosystem — millions of trained consultants worldwide and thousands of certified partner add-ons.',
            'S/4HANA — the modern, in-memory rewrite built for real-time business.',
          ],
        },
        {
          type: 'callout',
          variant: 'tip',
          title: 'Remember this',
          text:
            'When people say "SAP", they almost always mean either (a) the company, (b) the SAP ERP product — today that is S/4HANA, or (c) a specific SAP module such as SAP MM or SAP FICO. Context tells you which.',
        },
      ],
    },
    {
      id: 'm1-s4',
      title: 'SAP vs Other ERP Systems',
      readingTime: '20 min',
      blocks: [
        {
          type: 'p',
          text:
            'SAP is the market leader but it is not the only ERP. Knowing the landscape helps you pitch SAP intelligently and answer the inevitable "why SAP?" question.',
        },
        {
          type: 'table',
          headers: ['ERP', 'Sweet spot', 'Typical customer'],
          rows: [
            ['SAP S/4HANA', 'Large, multi-national, multi-industry', 'BMW, Nestlé, Unilever, Tata Steel'],
            ['Oracle Fusion Cloud ERP', 'Finance-led, cloud-first', 'Bank of America, Zoom'],
            ['Microsoft Dynamics 365', 'Mid-market, tight O365 integration', 'Coca-Cola bottlers, Chevron'],
            ['Infor CloudSuite', 'Industry-specific mid-market', 'Hospitality, fashion, equipment rental'],
            ['Workday', 'Best-of-breed HR &amp; Finance', 'Netflix, Airbnb'],
            ['NetSuite (Oracle)', 'Small &amp; mid-market', 'Start-ups and scale-ups'],
            ['Odoo / Tally / Zoho', 'SME and very small business', 'Local SMBs'],
          ],
        },
        { type: 'h', text: 'Why customers pick SAP' },
        {
          type: 'ul',
          items: [
            'End-to-end integration breadth — no other single vendor covers quite as much.',
            'Industry best practices baked in — less custom build, faster go-live.',
            'Real-time platform (HANA) — one database for transactions and analytics.',
            'Huge ecosystem of skilled consultants and partners worldwide.',
          ],
        },
        {
          type: 'callout',
          variant: 'warn',
          title: 'Honest trade-off',
          text:
            'SAP is powerful but not cheap and not simple. For a 50-person startup, Odoo or NetSuite will usually be a better fit. For a 50,000-person multinational, SAP is typically in the final shortlist.',
        },
      ],
    },
    {
      id: 'm1-s5',
      title: 'Real-World SAP Use Cases',
      readingTime: '20 min',
      blocks: [
        { type: 'h', text: 'Manufacturing — a global car maker' },
        {
          type: 'p',
          text:
            'A car plant in Stuttgart uses SAP PP to plan daily production, SAP MM to pull steel, tyres and electronics from 400+ global suppliers, and SAP SD to confirm delivery dates to dealers. SAP FICO consolidates the numbers in 20 countries overnight. Every VIN number, every part, every tax code — one system.',
        },
        { type: 'img', src: IMG.factory, caption: 'Figure 1.4 — Manufacturing lines orchestrated by SAP PP + MM.' },
        { type: 'h', text: 'Retail — an international supermarket chain' },
        {
          type: 'p',
          text:
            'SAP Retail handles 100,000 SKUs across 3,000 stores. Store POS transactions flow into SAP every night; replenishment orders are generated automatically; SAP FICO closes the books for each country in days, not weeks.',
        },
        { type: 'h', text: 'Banking — a Tier-1 bank' },
        {
          type: 'p',
          text:
            'Banks use SAP mostly for the "back-office": SAP FICO for general ledger, SAP HCM for 150,000 employees, SAP Ariba for procurement, and SAP Concur for expense management. The core banking system stays separate but feeds SAP daily.',
        },
        { type: 'img', src: IMG.finance, caption: 'Figure 1.5 — Financial services rely on SAP FICO for closing the books.' },
        { type: 'h', text: 'Public sector, utilities, oil &amp; gas' },
        {
          type: 'p',
          text:
            'Governments use SAP IS-PS for budgeting and grants; oil majors use SAP IS-Oil for joint-venture accounting and crude movement; utilities use SAP IS-U for meter-to-cash. The variety is enormous — and that is the whole point of SAP.',
        },
        {
          type: 'callout',
          variant: 'example',
          title: 'Try this thought experiment',
          text:
            'Pick any large company you know and ask: "Where would SAP fit here? Which modules would they use for HR, procurement, sales, manufacturing, finance?" If you can answer that, you already think like an SAP consultant.',
        },
      ],
    },
  ],
  quiz: [
    {
      q: 'What does ERP stand for?',
      options: [
        'Enterprise Resource Planning',
        'Electronic Reporting Protocol',
        'Enterprise Revenue Platform',
        'External Resource Pipeline',
      ],
      correct: 0,
      explanation: 'ERP = Enterprise Resource Planning — integrated software running core business functions.',
    },
    {
      q: 'Which of the following is the biggest reason companies adopt ERP?',
      options: [
        'Cheaper IT licence costs',
        'One integrated system with a single version of the truth',
        'Replace email entirely',
        'Avoid paying taxes',
      ],
      correct: 1,
      explanation: 'The core ERP value is integration — one system, one database, one version of the truth.',
    },
    {
      q: 'SAP was founded in which country and year?',
      options: ['USA, 1985', 'UK, 1965', 'Germany, 1972', 'Japan, 1980'],
      correct: 2,
      explanation: 'SAP was founded in 1972 in Walldorf, Germany, by five ex-IBM engineers.',
    },
    {
      q: 'Which SAP product line is the modern, in-memory ERP?',
      options: ['SAP R/2', 'SAP ECC 6.0', 'SAP S/4HANA', 'SAP Business One'],
      correct: 2,
      explanation: 'S/4HANA is SAP’s modern ERP running entirely on the in-memory HANA database.',
    },
    {
      q: 'A global car manufacturer would most likely use which SAP module to schedule production?',
      options: ['SAP SD', 'SAP PP', 'SAP HCM', 'SAP FICO'],
      correct: 1,
      explanation: 'SAP PP — Production Planning — handles manufacturing schedules and capacity.',
    },
    {
      q: 'Which of the following is NOT a typical competitor of SAP S/4HANA?',
      options: ['Oracle Fusion Cloud ERP', 'Microsoft Dynamics 365', 'Workday', 'Adobe Photoshop'],
      correct: 3,
      explanation: 'Adobe Photoshop is a design tool, not an ERP system.',
    },
  ],
};

// ——————————————————————————————————————————————————————————————
//  MODULE 2 — SAP Landscape & Architecture (4h)
// ——————————————————————————————————————————————————————————————
const module2 = {
  id: 'm2',
  number: 2,
  title: 'SAP Landscape & Architecture',
  duration: '4 Hours',
  minutes: 240,
  icon: 'Network',
  tagline: 'How SAP is physically organized — systems, servers and the GUI you actually click on.',
  takeaway:
    'You will be able to draw and explain the classical SAP three-system landscape, the three-tier architecture, and when to choose SAP GUI vs Fiori, on-premise vs cloud.',
  subTopics: [
    {
      id: 'm2-s1',
      title: 'SAP System Landscape (DEV / QA / PROD)',
      readingTime: '20 min',
      blocks: [
        {
          type: 'p',
          text:
            'No-one builds or tests changes directly in the live system that runs the business. SAP projects always use multiple environments — typically Development, Quality/Test and Production — often called the "three-system landscape".',
        },
        { type: 'img', src: landscapeDiagram, caption: 'Figure 2.1 — Changes always flow DEV → QA → PROD, never the other way.' },
        {
          type: 'table',
          headers: ['System', 'Purpose', 'Who works there'],
          rows: [
            ['DEV (Development)', 'Configuration &amp; custom code is built.', 'Consultants, ABAP developers'],
            ['QA / Test', 'Changes are tested — integration, user-acceptance, regression.', 'Business users, testers'],
            ['PROD (Production)', 'The live business runs here. No manual changes allowed.', 'End-users only'],
          ],
        },
        { type: 'h', text: 'Transports' },
        {
          type: 'p',
          text:
            'Every configuration change or program in DEV is packaged into a "Transport Request" and promoted forward — first to QA for testing, then (after approval) to PROD. This gives a clean audit trail of what went live, when, and by whom.',
        },
        {
          type: 'callout',
          variant: 'tip',
          title: 'Rule of thumb',
          text:
            'If a consultant ever suggests changing something directly in PROD, run. You never touch production.',
        },
      ],
    },
    {
      id: 'm2-s2',
      title: 'Three-Tier Architecture',
      readingTime: '20 min',
      blocks: [
        {
          type: 'p',
          text:
            'From R/3 onwards, SAP has used a classic three-tier client-server architecture: Presentation, Application and Database. Each tier can scale independently.',
        },
        { type: 'img', src: threeTierDiagram, caption: 'Figure 2.2 — The three logical layers of a SAP system.' },
        {
          type: 'table',
          headers: ['Tier', 'What it contains', 'Examples'],
          rows: [
            ['Presentation', 'The UI — what the user clicks on.', 'SAP GUI, SAP Fiori launchpad, SAP Business Client, mobile apps'],
            ['Application', 'Business logic — runs ABAP/Java code, coordinates requests.', 'Dispatcher, work processes, background jobs'],
            ['Database', 'Persisted data — master &amp; transaction.', 'SAP HANA, Oracle, MS SQL, DB2'],
          ],
        },
        {
          type: 'callout',
          variant: 'info',
          title: 'Why three tiers matter',
          text:
            'Because each layer is independent, you can add more application servers during peak season, upgrade the UI without touching the DB, or swap the DB underneath (many customers moved from Oracle to HANA this way).',
        },
      ],
    },
    {
      id: 'm2-s3',
      title: 'SAP GUI vs SAP Fiori',
      readingTime: '20 min',
      blocks: [
        {
          type: 'p',
          text:
            'The user interface you will actually look at depends on the SAP generation. The two big families are SAP GUI (classic, installed on Windows) and SAP Fiori (modern, browser-based, mobile-friendly).',
        },
        {
          type: 'table',
          headers: ['Aspect', 'SAP GUI', 'SAP Fiori'],
          rows: [
            ['Launched', '1990s', '2013'],
            ['Installation', 'Desktop client (SAPLogon)', 'Web browser / mobile'],
            ['Design', 'Form-heavy, transaction-centric', 'Role-based tiles, task-oriented'],
            ['Best for', 'Power users, accountants, super users', 'Managers, approvers, self-service, mobile'],
            ['Look &amp; feel', 'Grey, dense, thousands of fields', 'Clean, white, responsive'],
          ],
        },
        { type: 'img', src: IMG.dashboard, caption: 'Figure 2.3 — Fiori launchpad tiles give quick role-based access.' },
        { type: 'h', text: 'Which one wins?' },
        {
          type: 'p',
          text:
            'Both coexist. In most modern S/4HANA projects, simple transactions (leave request, approve PO, view KPIs) move to Fiori tiles while deep configuration and complex transactions stay in SAP GUI. Learning both is valuable.',
        },
      ],
    },
    {
      id: 'm2-s4',
      title: 'Cloud vs On-Premise SAP',
      readingTime: '25 min',
      blocks: [
        { type: 'h', text: 'On-premise' },
        {
          type: 'p',
          text:
            'Traditional model. The customer buys the licence, installs SAP in their own datacenter, runs it on their own servers, and is responsible for patching, upgrades, DR and security.',
        },
        { type: 'h', text: 'Cloud' },
        {
          type: 'p',
          text:
            'SAP hosts and runs the system on hyperscaler infrastructure (AWS/Azure/GCP) or its own datacenters and delivers it as a subscription. Two flavours exist: Public Cloud (shared, quarterly upgrades, less customization) and Private Cloud (dedicated tenant, more flexibility, RISE with SAP).',
        },
        {
          type: 'table',
          headers: ['Dimension', 'On-Premise', 'Public Cloud', 'Private Cloud (RISE)'],
          rows: [
            ['Upfront cost', 'High (licence + hardware)', 'Low (subscription)', 'Medium (subscription)'],
            ['Customization', 'Unlimited', 'Limited — use extensions', 'High'],
            ['Upgrades', 'Customer controlled', 'Quarterly, mandatory', 'Customer chooses window'],
            ['Ops responsibility', 'Customer', 'SAP', 'SAP'],
            ['Time to go-live', '12–24 months', '3–6 months', '6–12 months'],
          ],
        },
        {
          type: 'callout',
          variant: 'info',
          title: 'Industry direction',
          text:
            'SAP has publicly committed "Cloud First". New innovation, AI and analytics are delivered earliest on public cloud. Over the next decade most SAP customers will move to RISE or GROW with SAP.',
        },
        { type: 'img', src: IMG.datacenter, caption: 'Figure 2.4 — Modern SAP systems increasingly run on hyperscaler cloud.' },
      ],
    },
  ],
  quiz: [
    {
      q: 'What does the typical SAP three-system landscape consist of?',
      options: ['DEV, QA, PROD', 'BUILD, TEST, SHIP', 'ALPHA, BETA, GAMMA', 'AWS, Azure, GCP'],
      correct: 0,
      explanation: 'DEV → QA → PROD is the canonical landscape; transports flow forward only.',
    },
    {
      q: 'In the three-tier architecture, where does ABAP business logic execute?',
      options: ['Presentation layer', 'Application layer', 'Database layer', 'Network layer'],
      correct: 1,
      explanation: 'Business logic runs in Application-server work processes.',
    },
    {
      q: 'Which SAP UI is browser-based, role-based and mobile friendly?',
      options: ['SAP GUI', 'SAP Fiori', 'SAPLogon', 'SAP BusinessObjects'],
      correct: 1,
      explanation: 'Fiori is SAP’s modern, responsive, tile-based UI.',
    },
    {
      q: 'In SAP RISE, who is responsible for upgrades and basis operations?',
      options: ['The customer', 'A third-party system integrator', 'SAP', 'The CFO personally'],
      correct: 2,
      explanation: 'In RISE (private cloud), SAP runs, patches and upgrades the system on behalf of the customer.',
    },
    {
      q: 'Why are direct changes never done in the PROD system?',
      options: [
        'It is physically disabled',
        'Changes must be tested in QA first, and auditability requires controlled transports',
        'Production is read-only',
        'PROD only runs overnight',
      ],
      correct: 1,
      explanation: 'Changes must pass through DEV → QA before landing in PROD; this guarantees stability and audit trail.',
    },
  ],
};

// ——————————————————————————————————————————————————————————————
//  MODULE 3 — SAP Navigation & Basic Concepts (4h)
// ——————————————————————————————————————————————————————————————
const module3 = {
  id: 'm3',
  number: 3,
  title: 'SAP Navigation & Basic Concepts',
  duration: '4 Hours',
  minutes: 240,
  icon: 'Compass',
  tagline: 'Reading the screen like a pro — T-Codes, master data and the SAP org structure.',
  takeaway:
    'You will confidently read any SAP screen, interpret a T-Code, and understand the difference between master and transaction data, plus the key elements of SAP’s organizational structure.',
  subTopics: [
    {
      id: 'm3-s1',
      title: 'Anatomy of the SAP Screen',
      readingTime: '20 min',
      blocks: [
        {
          type: 'p',
          text:
            'Every SAP GUI screen follows the same skeleton. Once you know the parts, you can navigate any transaction in any module.',
        },
        {
          type: 'ul',
          items: [
            'Command field — top-left — type a T-Code and hit Enter to jump anywhere.',
            'Menu bar — File, Edit, Goto, System, Help (same everywhere).',
            'Standard toolbar — Save, Back, Exit, Cancel, Print, Find.',
            'Title bar — name of the current transaction.',
            'Application toolbar — buttons specific to the transaction you are in.',
            'Screen body — fields, tables, tabs you actually interact with.',
            'Status bar (bottom) — messages, system ID and client.',
          ],
        },
        {
          type: 'callout',
          variant: 'tip',
          title: 'Pro move',
          text:
            'In the command field, typing "/n" in front of a T-Code cancels the current transaction and jumps fresh. "/o" opens it in a new window. Consultants live and breathe "/n" and "/o".',
        },
      ],
    },
    {
      id: 'm3-s2',
      title: 'What Are T-Codes?',
      readingTime: '20 min',
      blocks: [
        {
          type: 'p',
          text:
            'T-Code (Transaction Code) is a short mnemonic that launches a specific screen in SAP. Instead of navigating menus four levels deep, you type the T-Code and you’re there instantly.',
        },
        {
          type: 'table',
          headers: ['T-Code', 'Module', 'What it does'],
          rows: [
            ['VA01', 'SD', 'Create Sales Order'],
            ['VA02 / VA03', 'SD', 'Change / Display Sales Order'],
            ['ME21N', 'MM', 'Create Purchase Order'],
            ['MIGO', 'MM', 'Goods Movement (receive / issue)'],
            ['MIRO', 'MM / FI', 'Enter Incoming Invoice'],
            ['FB01', 'FI', 'Post a Financial Document'],
            ['F110', 'FI', 'Automatic Payment Run'],
            ['XD01', 'SD', 'Create Customer Master'],
            ['MM01', 'MM', 'Create Material Master'],
            ['PA30', 'HCM', 'Maintain HR Master Data'],
            ['SU01', 'BASIS', 'Maintain Users'],
            ['SE16 / SE16N', 'ABAP', 'Generic Table Browser'],
          ],
        },
        {
          type: 'callout',
          variant: 'info',
          title: 'Pattern to remember',
          text:
            'T-Codes ending in 01 = Create, 02 = Change, 03 = Display. So VA01/VA02/VA03 or XK01/XK02/XK03 = Create/Change/Display Vendor. This pattern repeats across SAP.',
        },
      ],
    },
    {
      id: 'm3-s3',
      title: 'Master Data vs Transaction Data',
      readingTime: '20 min',
      blocks: [
        {
          type: 'p',
          text:
            'Everything in SAP is ultimately either master data (long-lived reference) or transaction data (day-to-day business events that reference master data).',
        },
        { type: 'img', src: dataTypesDiagram, caption: 'Figure 3.1 — Master vs Transaction data side by side.' },
        {
          type: 'ul',
          items: [
            'Master data = nouns (Customer 100200, Material ROH-STEEL-01, Employee 4782).',
            'Transaction data = verbs (Sales Order 5000123 for Customer 100200 on 5-Mar).',
            'Transactions always reference master data by its key.',
            'Bad master data ⇒ bad transactions ⇒ bad reports. Master data governance is a career in itself.',
          ],
        },
        {
          type: 'callout',
          variant: 'warn',
          title: 'Common pitfall',
          text:
            'Teams often rush master-data cleanup before go-live. The transactions that follow are only as good as the master data under them.',
        },
      ],
    },
    {
      id: 'm3-s4',
      title: 'Organizational Structure in SAP',
      readingTime: '25 min',
      blocks: [
        {
          type: 'p',
          text:
            'SAP mirrors the legal and operational reality of a company through a hierarchy of organizational units. Every transaction inherits this context.',
        },
        {
          type: 'table',
          headers: ['Org unit', 'Meaning', 'Example'],
          rows: [
            ['Client', 'Top-level tenant — completely separate set of data.', '100 = Production client'],
            ['Company Code', 'A legal entity that closes its own books.', '1000 = ACME India Ltd.'],
            ['Plant', 'A physical location where materials are produced / stocked.', '1100 = Pune factory'],
            ['Storage Location', 'Sub-area of a plant where stock is held.', '1101 = Raw materials'],
            ['Sales Org', 'Unit responsible for selling products.', 'IN01 = India sales'],
            ['Distribution Channel', 'How goods reach customers.', 'RE = Retail, WH = Wholesale'],
            ['Division', 'Product-line grouping.', '01 = Electronics, 02 = Appliances'],
            ['Purchasing Org', 'Negotiates with vendors.', 'P001 = Central procurement'],
            ['Controlling Area', 'Boundary for internal cost accounting.', 'CA01'],
          ],
        },
        {
          type: 'callout',
          variant: 'example',
          title: 'Putting it together',
          text:
            'A Sales Order in SAP says: "Sales Org IN01 / Distribution Channel RE / Division 01 / Plant 1100 / Company Code 1000 is selling Material FG-TV-55 to Customer 100200." Every report and balance can later be sliced on any of these dimensions.',
        },
      ],
    },
  ],
  quiz: [
    {
      q: 'What is a T-Code in SAP?',
      options: [
        'A tax rate',
        'A short code that opens a specific transaction screen',
        'A type of user license',
        'A database table',
      ],
      correct: 1,
      explanation: 'T-Code = Transaction Code — a shortcut to jump directly to a screen.',
    },
    {
      q: 'VA01 is used to…',
      options: ['Display a Sales Order', 'Change a Sales Order', 'Create a Sales Order', 'Create a Material'],
      correct: 2,
      explanation: 'VA01 = Create Sales Order (01 = Create, 02 = Change, 03 = Display).',
    },
    {
      q: 'Which is an example of MASTER data?',
      options: ['Purchase Order 4500001', 'Vendor 300100', 'Sales Invoice 9000555', 'Goods Receipt 5000234'],
      correct: 1,
      explanation: 'Vendor, customer, material, employee are master records; POs/invoices/GRs are transactional.',
    },
    {
      q: 'Which SAP org unit represents a legal entity that closes its own books?',
      options: ['Plant', 'Company Code', 'Sales Org', 'Storage Location'],
      correct: 1,
      explanation: 'Company Code is the legal entity — it issues balance sheets.',
    },
    {
      q: 'Prefixing a T-Code with "/n" in the command field…',
      options: [
        'Cancels the current transaction and starts this one fresh',
        'Opens it in a new window',
        'Locks the transaction',
        'Runs it in debug mode',
      ],
      correct: 0,
      explanation: '"/n" cancels the current tx and jumps; "/o" opens a new session.',
    },
  ],
};

export const MODULES_PART1 = [module1, module2, module3];
